import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ShoppingCart } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { scrollToHashTarget } from '@/lib/heroNavScroll';

const NAV_LINKS = [
  { label: 'Product', href: '#product' },
  { label: 'Ingredients', href: '#ingredients' },
  { label: 'Nutrition', href: '#nutrition' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

const PRODUCT_CATEGORIES = [
  { id: 'g-drink', label: 'G Drink', route: '/' },
  { id: 'caprisun', label: 'Caprisun', route: '/caprisun' },
  { id: 'stimorol', label: 'Stimorol', route: '/stimorol' },
  { id: 'durukan', label: 'Durukan', route: '/durukan' },
  { id: 'gandour', label: 'Gandour', route: null },
];

interface NavbarProps {
  currentProduct?: string;
  onProductChange?: (productId: string) => void;
}

const Navbar = ({ currentProduct = 'g-drink', onProductChange }: NavbarProps) => {
  const navigate = useNavigate();
  const { totalItems, toggleCart } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = NAV_LINKS.map((link) => link.href.replace('#', ''));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    scrollToHashTarget(href);
  };

  const handleProductSelect = (productId: string) => {
    const category = PRODUCT_CATEGORIES.find(p => p.id === productId);
    if (!category?.route && category?.id !== currentProduct) return; // Gandour disabled
    if (category?.route) navigate(category.route);
    else onProductChange?.(productId);
  };

  const currentProductLabel = PRODUCT_CATEGORIES.find(p => p.id === currentProduct)?.label || 'G Drink';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled ? 'bg-background/80 backdrop-blur-lg border-b border-border' : 'bg-transparent'
    }`}>
      <div className="px-6 md:px-12 lg:px-16 py-4">
        <div className="flex items-center justify-between">
          {/* Left side: Logo + Products Dropdown */}
          <div className="flex items-center gap-4">
            <a href="#" className="flex items-center gap-2">
              <span className="text-xs font-medium tracking-[0.2em] text-muted-foreground">DLC</span>
              <span className="font-display text-3xl text-primary">G</span>
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-background hover:bg-primary/10 transition-colors text-sm font-medium text-foreground">
                {currentProductLabel}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-50 bg-background border border-border rounded-lg shadow-lg min-w-[160px]" align="start">
                {PRODUCT_CATEGORIES.map((product) => (
                  <DropdownMenuItem
                    key={product.id}
                    onClick={() => handleProductSelect(product.id)}
                    disabled={!product.route && product.id !== currentProduct}
                    className={`cursor-pointer ${
                      currentProduct === product.id ? 'text-primary bg-primary/10' : 'text-foreground hover:bg-muted'
                    } ${!product.route ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {currentProduct === product.id && <span className="mr-2 text-primary">●</span>}
                    {product.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-medium transition-colors duration-200 ${
                  activeSection === link.href.replace('#', '') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </button>
            ))}
            {/* Cart icon */}
            <button onClick={() => toggleCart(true)} aria-label="Open cart" className="relative p-2 text-muted-foreground hover:text-foreground transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
          </div>

          {/* Mobile: Cart + Menu */}
          <div className="flex items-center gap-2 md:hidden">
            <button onClick={() => toggleCart(true)} className="relative p-2 text-foreground">
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="w-6 h-6 text-foreground" /> : <Menu className="w-6 h-6 text-foreground" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex flex-col py-4">
              <div className="px-6 pb-3 mb-2 border-b border-border">
                <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">Brand</p>
                <div className="grid grid-cols-2 gap-2">
                  {PRODUCT_CATEGORIES.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleProductSelect(product.id);
                      }}
                      disabled={!product.route && product.id !== currentProduct}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-colors min-h-[44px] ${
                        currentProduct === product.id
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border text-foreground'
                      } ${!product.route ? 'opacity-40' : ''}`}
                    >
                      {product.label}
                    </button>
                  ))}
                </div>
              </div>
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className={`px-6 py-4 text-left text-base font-medium transition-colors min-h-[48px] ${
                    activeSection === link.href.replace('#', '') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
